import { ROUTES } from '@/constants/routes';

// ── localStorage helpers ─────────────────────────────────────────────────────

/**
 * Reads a persisted integer from localStorage.
 * Falls back to `defaultValue` when the key is absent, non-numeric, or
 * localStorage is unavailable (SSR / restricted context).
 */
export function readStoredWidth(key: string, defaultValue: number): number {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
  } catch {
    // localStorage may be unavailable
  }
  return defaultValue;
}

// ── URL / search-param keys ───────────────────────────────────────────────────

/** Search-param key used to open a specific console tab on page load. */
export const CONSOLE_TAB_PARAM = 'openConsoleTab';

/** Search-param key used to deep-link a specific workflow run on page load. */
export const RUN_ID_PARAM = 'runId';

/** Path to a workflow, optionally deep-linked to a specific run. */
export function workflowRunPath(workflowId: string, runId?: string): string {
  const base = `${ROUTES.WORKFLOW.ROOT}/${workflowId}`;
  return runId ? `${base}?${RUN_ID_PARAM}=${runId}` : base;
}

// ── localStorage keys ────────────────────────────────────────────────────────

/** Persisted pixel width of the console (right) sidebar panel. */
export const CONSOLE_PANEL_WIDTH_KEY = 'workflow-console-panel-width';

/** Persisted pixel width of the node-explorer (left) sidebar panel. */
export const NODE_EXPLORER_WIDTH_KEY = 'workflow-node-explorer-panel-width';

/** Persisted interaction mode ('mouse' | 'hand') for the canvas. */
export const INTERACTION_MODE_KEY = 'workflow_interaction_mode';

// ── Panel sizes (pixels) ──────────────────────────────────────────────────────

export const DEFAULT_CONSOLE_PANEL_WIDTH = 340;
export const DEFAULT_NODE_EXPLORER_PANEL_WIDTH = 380;

/** Minimum draggable width of the console (right) sidebar. */
export const CONSOLE_PANEL_MIN_WIDTH = 340;
/** Maximum draggable width of the console (right) sidebar. */
export const CONSOLE_PANEL_MAX_WIDTH = 900;

/** Minimum draggable width of the node-explorer (left) sidebar. */
export const NODE_EXPLORER_PANEL_MIN_WIDTH = 380;
/** Maximum draggable width of the node-explorer (left) sidebar. */
export const NODE_EXPLORER_PANEL_MAX_WIDTH = 800;

// ── Node canvas defaults (pixels) ────────────────────────────────────────────

/**
 * Fallback node width used when `node.measured.width` is not yet available
 * (e.g. newly added node, auto-layout pass, visibility calculations).
 */
export const DEFAULT_NODE_WIDTH = 300;

/**
 * Fallback node height used when `node.measured.height` is not yet available.
 */
export const DEFAULT_NODE_HEIGHT = 200;

/** Pixel padding around nodes used when routing orthogonal edges. */
export const ORTHO_EDGE_NODE_PADDING = 20;

/** Corner radius (px) for orthogonal edge path segments. */
export const ORTHO_EDGE_CORNER_RADIUS = 8;

// ── Auto-layout spacing (pixels) ─────────────────────────────────────────────

/** Vertical gap between nodes in the auto-layout algorithm. */
export const AUTO_LAYOUT_VERTICAL_GAP = 80;

/** Horizontal gap between nodes in the auto-layout algorithm. */
export const AUTO_LAYOUT_HORIZONTAL_GAP = 120;

// ── Workflow thumbnail ────────────────────────────────────────────────────────

/** Screenshot capture width (px) for workflow thumbnails. */
export const THUMBNAIL_WIDTH = 1024;

/** Screenshot capture height (px) for workflow thumbnails. */
export const THUMBNAIL_HEIGHT = 768;

// ── Office document embed ─────────────────────────────────────────────────────

/** How long (ms) to wait for an Office document embed to load before giving up. */
export const OFFICE_EMBED_LOAD_TIMEOUT_MS = 25_000;

/** Upper bound (ms) for the loading overlay shown on Office document embeds. */
export const OFFICE_EMBED_OVERLAY_MAX_MS = 60_000;

// ── Panel event display ───────────────────────────────────────────────────────

/** Maximum characters shown before truncating streamed / event text in the UI. */
export const EVENT_DISPLAY_MAX_LENGTH = 300;

// ── Layout breakpoints ────────────────────────────────────────────────────────

/**
 * Canvas container width (px) below which bottom-bar controls collapse into
 * condensed mode to avoid overlapping each other.
 */
export const CONDENSED_BREAKPOINT = 1024;

/** Returns true when the canvas is narrow enough to use condensed controls. */
export const isCondensedLayout = (panelGroupWidth?: number): boolean =>
  panelGroupWidth !== undefined && panelGroupWidth < CONDENSED_BREAKPOINT;
